import { css, cx, etiColors, mediaQueries } from '@eti/styles';
import Media from 'react-media';
import { useTranslation, useDirection } from '@eti/providers';
import { DangerousHtmlContent, Pictogram } from '@eti/components';
import { Check } from '@phosphor-icons/react';
import { Heading, vars } from '@etg/wings';
import checkIcon from '@eti/assets/icons/checkList.svg?url'; // eslint-disable-line import/no-unresolved

import { DYNAMIC_ICON } from '../utils/dynamic-icons/icons';
import { useDynamicIcon } from '../utils/dynamic-icons/useDynamicIcon';
import { getProductPictogramId } from '../utils/icon/getProductImageryIds';
import { FLEXIBLE_TICKET } from '../../products';
import { isPrimaryFlow } from '../utils/primaryFlow';
import ReadMoreOld from './ProductReadMoreOld';
import UpSell from './ProductUpSell';
import ProductIcon from './ProductIcon';
import ProductLogo from './ProductLogo';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const wrapperStyles = isNewCardDesignEnabled => css`
  grid-column: 2;
  grid-row: 1;
  padding: 16px;
  padding-inline-start: 0;
  word-break: break-word;

  @media ${mediaQueries.small.only} {
    padding-inline-start: 16px;
  }

  ul > *:not(:last-child) {
    margin-bottom: 8px;
  }

  ${!isNewCardDesignEnabled && css`
    padding: 0;
  `};
`;
const innerWrapperStyles = css`
  font-size: 0.875rem;
  width: 100%;
`;
const productIconStyles = css`
  flex-shrink: 0;
  height: 40px;
  margin-right: 1rem;
  width: 40px;

  [dir='rtl'] & {
    margin-left: 1rem;
    margin-right: 0;
  }
`;
const iconStyles = css`
  height: 30px;
  position: absolute;
  width: 30px;
`;
const pictogramStyles = css`
  margin-right: 8px;
  margin-top: 2px;

  html[dir='rtl'] & {
    margin-left: 8px;
    margin-right: 0;
  }
`;
const infantNoteCheckIcon = css`
  height: 16px;
  transform: translateY(2px);
  width: 16px;
`;
const titleAndPictogramWrapperStyles = css`
  display: flex;
  margin-bottom: 8px;
  margin-top: 8px;
`;
const titleStyles = (showImage, isNewCardDesignEnabled) => css`
  align-items: center;
  color: #000;
  font-weight: 700;

  ${!showImage && !isNewCardDesignEnabled && 'margin-left: 35px'};

  html[dir='rtl'] & {
    ${!showImage && !isNewCardDesignEnabled && ` margin-left: 0;
        margin-right: 35px;
      `}
  }

  @media ${mediaQueries.medium.up} {
    font-size: 1.375rem;
  }
`;
const textContent = iconContent => css`
  > *:not(:last-child) {
    &:not(a) {
      margin-bottom: 16px;
    }
  }

  & p {
    font-size: 0.875rem;

    @media (min-width: 48em) {
      line-height: 150%;
    }
  }

  & li {
    padding-inline-start: 24px;
    position: relative;

    [dir='rtl'] & {
      padding-left: 0;
    }

    &::before {
      background: url(${checkIcon});
      background-repeat: no-repeat;
      background-size: contain;
      content: '';
      height: 16px;
      inset-inline-start: 0;
      margin-inline-end: 8px;
      position: absolute;
      top: 3px;
      width: 16px;
    }
  }

  & a {
    color: ${vars.colors.links.default};
    display: block;
    margin-top: 4px;
    position: relative;
    width: fit-content;

    &::after {
      content: ${iconContent !== null && iconContent !== void 0 ? iconContent : ''};
      margin-inline-start: 2px;
      position: absolute;
      top: -1px;
    }
  }
`;
const descriptionStyles = linkIconContent => css`
  color: ${etiColors.black};
  margin-bottom: 0.375rem;
  ${textContent(linkIconContent)};
`;
const additionalInformationStyles = linkIconContent => css`
  color: ${etiColors.black};
  ${textContent(linkIconContent)};
`;
const infoTextStyles = css`
  color: ${etiColors.black};
  font-size: 0.875rem;
  position: relative;
  top: -10px;

  @media ${mediaQueries.medium.up} {
    top: -1rem;
  }
`;
const infantNoteWrapperStyles = css`
  display: flex;
  flex-direction: row;
  margin-top: 16px;
`;
const noteStyles = css`
  color: ${etiColors.black};
  display: block;
  font-size: 0.875rem;
  padding-inline-start: 8px;
`;
const ProductDescription = _ref => {
  let {
    additionalContent,
    additionalInformation,
    brand,
    description,
    hasInfants,
    hasLogo = false,
    infantNoteLabel,
    isNewCardDesignEnabled = false,
    productName,
    readMore,
    shouldShowImage,
    shouldShowMandatoryChoiceText,
    title,
    upSell
  } = _ref;
  const {
    isRTL
  } = useDirection();
  const {
    t
  } = useTranslation();
  const shouldDisplayUpsell = upSell && upSell.content !== '' && isPrimaryFlow();
  const [icon] = useDynamicIcon(vars.colors.links.default, DYNAMIC_ICON.linkIcon);
  return /*#__PURE__*/_jsxs("div", {
    className: wrapperStyles(isNewCardDesignEnabled),
    children: [/*#__PURE__*/_jsxs("div", {
      className: innerWrapperStyles,
      "data-testid": `${productName}-product-description`,
      children: [shouldDisplayUpsell && /*#__PURE__*/_jsx(UpSell, {
        "data-testid": "upsell-text",
        upSell: upSell
      }), /*#__PURE__*/_jsxs("div", {
        className: titleAndPictogramWrapperStyles,
        children: [!shouldShowImage && !isNewCardDesignEnabled && /*#__PURE__*/_jsx(ProductIcon, {
          className: cx(productIconStyles, iconStyles),
          id: productName,
          type: "inverted"
        }), /*#__PURE__*/_jsx(Media, {
          query: mediaQueries.small.only,
          children: isMobile => {
            var _getProductPictogramI;
            return isNewCardDesignEnabled && (!shouldShowImage || isMobile) && /*#__PURE__*/_jsx(Pictogram, {
              brand: brand,
              className: pictogramStyles,
              "data-testid": `${productName}-pictogram`,
              id: (_getProductPictogramI = getProductPictogramId(productName, isRTL)) !== null && _getProductPictogramI !== void 0 ? _getProductPictogramI : getProductPictogramId('fallback', isRTL),
              size: "2rem"
            });
          }
        }), /*#__PURE__*/_jsx(Heading, {
          className: titleStyles(shouldShowImage, isNewCardDesignEnabled),
          level: 2,
          children: title
        })]
      }), shouldShowMandatoryChoiceText && !isNewCardDesignEnabled && /*#__PURE__*/_jsx("p", {
        className: infoTextStyles,
        children: t('TravelerDetails.ProductSection.MandatoryChoiceText')
      }), description && /*#__PURE__*/_jsx(DangerousHtmlContent, {
        className: descriptionStyles(icon),
        content: description
      }), additionalInformation && /*#__PURE__*/_jsx(DangerousHtmlContent, {
        className: additionalInformationStyles(icon),
        content: additionalInformation
      }), hasInfants && infantNoteLabel && isNewCardDesignEnabled && productName === FLEXIBLE_TICKET.name && /*#__PURE__*/_jsxs("div", {
        className: infantNoteWrapperStyles,
        children: [/*#__PURE__*/_jsx(Check, {
          className: infantNoteCheckIcon,
          color: vars.colors.signal.positive.foreground,
          "data-testid": "included-checks"
        }), /*#__PURE__*/_jsx("span", {
          className: cx(noteStyles, `${productName}-infant-note-label`),
          children: infantNoteLabel
        })]
      }), (readMore === null || readMore === void 0 ? void 0 : readMore.isEnabled) && !isNewCardDesignEnabled && /*#__PURE__*/_jsx(ReadMoreOld, {
        content: readMore.content,
        isNewCardDesignEnabled: isNewCardDesignEnabled,
        modalTitle: title,
        productName: productName
      }), !shouldShowImage && hasLogo && /*#__PURE__*/_jsx(ProductLogo, {
        productName: productName
      })]
    }), additionalContent]
  });
};
export default ProductDescription;
//# sourceMappingURL=ProductDescription.js.map